import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import useListboxKeys from "../../utils/hooks/useListboxKeys";

// Searchable multi-select. `options` = [{ value, label }]; `value` = array of
// selected values; `onChange(nextValues)`. Selected items render as removable
// chips inside the control; typing filters the dropdown. The dropdown is
// portalled to <body> and positioned against the control, so it is never
// clipped by a scrolling/overflow parent (e.g. a modal body).
export default function MultiSelect({
  options = [],
  value = [],
  onChange,
  disabled = false,
  placeholder = "",
  emptyText = "",
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [rect, setRect] = useState(null);
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const updateRect = () => {
      const el = rootRef.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      setRect({
        left: r.left,
        top: r.bottom + 4,
        width: r.width,
        maxHeight: Math.max(140, window.innerHeight - r.bottom - 16),
      });
    };
    updateRect();

    const onDocMouseDown = (e) => {
      if (
        (rootRef.current && rootRef.current.contains(e.target)) ||
        (listRef.current && listRef.current.contains(e.target))
      ) {
        return;
      }
      setOpen(false);
      setQuery("");
    };
    // Capture phase so we also catch scrolling of the modal body, not just window.
    window.addEventListener("scroll", updateRect, true);
    window.addEventListener("resize", updateRect);
    document.addEventListener("mousedown", onDocMouseDown);
    return () => {
      window.removeEventListener("scroll", updateRect, true);
      window.removeEventListener("resize", updateRect);
      document.removeEventListener("mousedown", onDocMouseDown);
    };
  }, [open, listRef]);

  const labelByValue = useMemo(
    () => Object.fromEntries(options.map((o) => [o.value, o.label])),
    [options],
  );
  const selectedSet = useMemo(() => new Set(value), [value]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, query]);

  const remove = useCallback(
    (val) => onChange(value.filter((v) => v !== val)),
    [onChange, value],
  );
  const toggle = useCallback(
    (val) => (selectedSet.has(val) ? remove(val) : onChange([...value, val])),
    [onChange, remove, selectedSet, value],
  );

  // A pick keeps the menu open — the whole point of this control is picking
  // several — so unlike the single-selects nothing closes on Enter.
  const {
    activeIndex,
    setActiveIndex,
    onKeyDown: onListboxKeyDown,
    listboxId,
    optionId,
    activeDescendant,
  } = useListboxKeys({
    open: open && !disabled,
    setOpen,
    triggerRef,
    listRef,
    count: filtered.length,
    onSelect: (i) => toggle(filtered[i].value),
  });

  const onKeyDown = (e) => {
    if (e.key === "Backspace" && !query && value.length) {
      remove(value[value.length - 1]);
      return;
    }
    onListboxKeyDown(e);
  };

  const menu =
    open && !disabled && rect
      ? createPortal(
          <div
            style={{
              position: "fixed",
              left: rect.left,
              top: rect.top,
              width: rect.width,
              maxHeight: rect.maxHeight,
              zIndex: 1000,
            }}
            id={listboxId}
            ref={listRef}
            role="listbox"
            aria-multiselectable="true"
            className="overflow-y-auto rounded-xl border border-slate-200 bg-white shadow-lg py-1"
          >
            {filtered.length === 0 ? (
              <p role="status" className="px-3 py-2 text-xs text-slate-400">
                {emptyText}
              </p>
            ) : (
              filtered.map((o, i) => {
                const checked = selectedSet.has(o.value);
                return (
                  <button
                    key={o.value}
                    id={optionId(i)}
                    data-lb-index={i}
                    type="button"
                    role="option"
                    tabIndex={-1}
                    aria-selected={checked}
                    onClick={() => toggle(o.value)}
                    onMouseMove={() => setActiveIndex(i)}
                    className={`flex items-center justify-between w-full px-3 py-1.5 text-sm text-left ${
                      checked ? "text-indigo-700 font-semibold" : "text-slate-700"
                    } ${i === activeIndex ? "bg-slate-100" : ""}`}
                  >
                    <span className="truncate">{o.label}</span>
                    {checked && (
                      <svg
                        className="w-4 h-4 text-indigo-600 shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                      </svg>
                    )}
                  </button>
                );
              })
            )}
          </div>,
          document.body,
        )
      : null;

  return (
    <div ref={rootRef} className="relative">
      <div
        className={`flex flex-wrap items-center gap-1.5 w-full px-2 py-1.5 rounded-xl border bg-slate-50 transition ${
          disabled
            ? "opacity-60 border-slate-200"
            : "border-slate-200 focus-within:border-indigo-500 focus-within:ring-2 focus-within:ring-indigo-500/20 focus-within:bg-white"
        }`}
      >
        {value.map((val) => (
          <span
            key={val}
            className="inline-flex items-center gap-1 pl-2.5 pr-1 py-1 rounded-lg bg-indigo-600 text-white text-xs font-bold"
          >
            {labelByValue[val] ?? val}
            <button
              type="button"
              disabled={disabled}
              onClick={() => remove(val)}
              aria-label={t("common.remove", { label: labelByValue[val] ?? val })}
              className="rounded p-0.5 hover:bg-white/20 disabled:opacity-50"
            >
              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </span>
        ))}
        <input
          type="text"
          ref={triggerRef}
          role="combobox"
          aria-expanded={open && !disabled}
          aria-controls={open && !disabled ? listboxId : undefined}
          aria-activedescendant={activeDescendant}
          aria-autocomplete="list"
          value={query}
          disabled={disabled}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder={value.length ? "" : placeholder}
          className="flex-1 min-w-[6rem] border-0 bg-transparent text-sm text-slate-700 outline-none focus:ring-0 px-1 py-0.5 disabled:cursor-not-allowed"
        />
      </div>
      {menu}
    </div>
  );
}
